import { MinzeElement, Reactive, EventListeners } from 'minze'
import { MODALS } from '../../constants'
import { Locale } from '../../modules/locale'
import { AdditionalTypes } from '../../modules/additionalTypes'
import {
  AbstractSubscriber,
  AbstractSearchCore,
  IFacet,
  IFacetOption,
  ISearchConfig,
} from '../../modules/search'
import MainModule from '../../modules/main'
import { Font, SCREEN_WIDTHS } from '../../constants'
import { getVisibleWidth } from '../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'disciplinesSection', {
  see: 'Voir les ',
  events: ' événements',
  title: 'Des sorties, on en a de tous les genres. De quoi avez-vous envie?',
})

Locale.getInstance().registerKeySet('en', 'disciplinesSection', {
  see: 'See ',
  events: ' events',
  title: 'We’ve got all kinds of experiences. What are you in the mood for?',
})

export interface LvcDisciplinesSection {
  selectedCategories: string[]
  subcategories: any[]
}

class LvcDisciplinesSectionSubscriber extends AbstractSubscriber {
  private _updateCallback: (data: any) => void
  private _core: AbstractSearchCore | null = null
  constructor(groupId: string, updateCallback: (data: any) => void) {
    super(groupId)
    this._updateCallback = updateCallback
  }

  async attached(core: AbstractSearchCore): Promise<void> {
    this._core = core
  }

  detaching(): void {
    this._core = null
  }

  public async setLocation(location: any): Promise<void> {
    let searchConfig: ISearchConfig =
      (this._core?.searchConfig as ISearchConfig) || ({} as ISearchConfig)
    let geoIndexes = this._core?.getIndexesWithGeo() ?? []

    if (searchConfig) {
      searchConfig.options = searchConfig?.options ?? {}
      searchConfig.options.aroundLatLng = searchConfig.options.aroundLatLng ?? {}
      for (let i = 0; i < geoIndexes.length; i++) {
        searchConfig.options.aroundLatLng[geoIndexes[i]] =
          `${location.latitude},${location.longitude}`
      }
    }

    await this._core?.applySearchConfig(searchConfig)
  }

  public async setDisciplines(disicplines: string[]): Promise<void> {
    let searchConfig: ISearchConfig =
      (this._core?.searchConfig as ISearchConfig) || ({} as ISearchConfig)
    let disciplineFacet = searchConfig.facets?.find(
      facet => facet.name === 'uiAdditionalTypesCode'
    ) as IFacet

    if (!searchConfig.facets) {
      searchConfig.facets = []
    }

    if (!disciplineFacet) {
      disciplineFacet = {
        name: 'uiAdditionalTypesCode',
        options: [] as IFacetOption[],
      } as IFacet
      searchConfig.facets.push(disciplineFacet)
    }

    disciplineFacet.options = disicplines.map(disicpline => {
      return {
        value: disicpline,
        selected: true,
        available: true,
      } as IFacetOption
    }) as IFacetOption[]

    await this._core?.applySearchConfig(searchConfig)
  }

  async onSearchRequest(): Promise<void> {
    await this._core?.goToSearch()
  }

  public async getEventCount(): Promise<number> {
    let searchConfig: ISearchConfig =
      (this._core?.searchConfig as ISearchConfig) || ({} as ISearchConfig)

    let results = await this._core?.executeSearch(searchConfig)
    let numberOfResults = 0

    for (let index in results) {
      numberOfResults += results[index].total
    }
    return numberOfResults
  }

  async onSearchConfigUpdate(data: any): Promise<void> {
    this._updateCallback(data)
  }

  async onSearchTextUpdate(): Promise<void> {}

  async onFacetUpdate(): Promise<void> {}

  async onResultUpdate(): Promise<void> {}
}

export class LvcDisciplinesSection extends MinzeElement {
  dataGroupId: string = 'lvcDisciplinesSection'
  reactive: Reactive = [
    ['selectedCategories', []],
    ['additionalTypes', {}],
    ['list', []],
  ]
  additionalTypes: any = {}
  list: any[] = []
  lvcDisicplinesSubscriber: LvcDisciplinesSectionSubscriber | null = null
  eventCount: number | null = null

  openDisciplinesDropdown = () => {
    MainModule.getInstance().showModal(MODALS.DISCIPLINES_DROPDOWN)
  }

  async onCategoryClick(e: Event): Promise<void> {
    const target = e.target as HTMLElement
    const chip = target.closest('.category-chip') as HTMLElement
    if (chip) {
      const value = chip.getAttribute('value')
      if (this.selectedCategories.includes(value as string)) {
        let idx = this.selectedCategories.indexOf(value as string)
        this.selectedCategories.splice(idx, 1)
      } else {
        this.selectedCategories.push(value as string)
      }

      this.lvcDisicplinesSubscriber?.setDisciplines(this.selectedCategories)
      const eventCountPromise = this.lvcDisicplinesSubscriber?.getEventCount()
      if (eventCountPromise) {
        this.eventCount = await eventCountPromise
        this.rerender()
      }
    }
  }

  onApplyClick = () => {
    this.lvcDisicplinesSubscriber?.onSearchRequest()
  }

  closeModal = () => {
    MainModule.getInstance().hideModal(MODALS.DISCIPLINES_DROPDOWN)
  }

  async onReady(): Promise<void> {
    this.additionalTypes = await AdditionalTypes.getInstance().getAllInTree()

    this.list = await AdditionalTypes.getInstance().getAll()
    this.lvcDisicplinesSubscriber = new LvcDisciplinesSectionSubscriber(
      this.dataGroupId as string,
      this.handleSearchUpdate.bind(this)
    )
    MainModule.getInstance().getSearchManager().subscribe(this.lvcDisicplinesSubscriber)
    this.eventCount = (await this.lvcDisicplinesSubscriber?.getEventCount()) || null

    let mainModule = MainModule.getInstance()
    mainModule.getEE().on('geo:location', location => {
      this.handleLocationChange()
    })
    this.handleLocationChange()
    this.rerender()
  }

  async handleLocationChange(): Promise<void> {
    let location = await MainModule.getInstance().getGeo().currentLocation

    this.lvcDisicplinesSubscriber?.setLocation(location)
  }

  async handleSearchUpdate(data: any): Promise<void> {
    if (this.selectedCategories.length === 0) {
      let facet = data.facets.find((facet: IFacet) => facet.name === 'uiAdditionalTypesCode')

      if (facet) {
        facet.options
          .filter((option: IFacetOption) => option.selected)
          .forEach((option: IFacetOption) => {
            if (!this.selectedCategories.includes(option.value)) {
              this.selectedCategories.push(option.value)
            }
          })
      }
    }
  }

  getDisciplineLabel(id: string) {
    let item = this.list.find((item: any) => item.code == id)

    return item ? item.label : ''
  }

  renderCategories = (categories: any[]) => {
    let renderedCategories = []
    for (let k in categories) {
      let category = categories[k]
      renderedCategories.push(
        `<lvc-chip
              theme="dark-borderless"
              width="fit-content"
              ${getVisibleWidth() >= SCREEN_WIDTHS.DESKTOP_MEDIUM ? `size="large"` : ''}
              value="${category.code}"
              class="category-chip" ${
                this.selectedCategories.includes(category.code) ? 'selected=true' : 'selected=false'
              }
              enable-hover-animation="true"
            >
              ${category.label}
            </lvc-chip>`
      )
    }

    return `
      <lvc-box display="flex" justifyContent="center" flexWrap="wrap" columnGap="8px" rowGap="8px" mb="60px">
        ${renderedCategories.join('')}
      </lvc-box>
    `
  }

  html = () => /* html */ `
    <lvc-box display="flex" justifyContent="center" flexDirection="column" alignItems="center" textAlign="center" columnGap="8px" rowGap="8px" mb="16px">
      <lvc-charlie-peacesign-icon width="225" height="225"></lvc-charlie-peacesign-icon>
      <h4 class="disciplines-title">${Locale.getKey('disciplinesSection.title')}</h4>
    </lvc-box>
    ${this.renderCategories(this.additionalTypes as any[])}
    <lvc-box display="flex" justifyContent="center" flexWrap="wrap" columnGap="8px" rowGap="8px" mb="14px">
      <lvc-button enable-hover-box-shadow="true" padding="0 15px" class="apply-button">${Locale.getKey('disciplinesSection.see')}<b class="eventCount">${this.eventCount !== null ? this.eventCount : ''}</b>${Locale.getKey('disciplinesSection.events')}</lvc-button>
    </lvc-box>
  `

  css = () => `
    .disciplines-title {
      color: var(--brand-minuit, #1E007D);
      text-align: center;
      font-family: ${Font.family};
      font-variant-numeric: lining-nums tabular-nums;
      font-feature-settings: 'clig' off, 'liga' off;
      font-size: 40px;
      font-style: normal;
      font-weight: ${Font.weight.medium};
      line-height: 120%; /* 48px */
      max-width: 730px;
   }
   .eventCount{
    color: var(--brand-spotlight, #FF4600);
   }

    @media screen and (max-width: 1024px) {
      .disciplines-title {
        font-size: 20px;
      }
    }
  `

  eventListeners: EventListeners = [
    ['.apply-button', 'click', this.onApplyClick.bind(this)],
    ['.category-chip', 'click', this.onCategoryClick.bind(this)],
  ]
}
